/**
 * The server's clock, tracked from every response.
 *
 * The freshness ladder asks "how old is this data", and the honest answer is
 * `serverNow − observedAt`. The client's own clock cannot stand in for
 * `serverNow`: a laptop five minutes fast would light an amber banner on
 * perfectly fresh data, and one five minutes slow would never light one at
 * all.
 *
 * Estimating the offset from `observedAt` itself does not work either. If
 * every response we have ever seen was eight minutes old, "the server is
 * eight minutes behind" and "the data is eight minutes stale" are the same
 * observation, and the ladder would never fire — which is precisely the case
 * it exists for.
 *
 * So the server states its clock, in the `Date` header RFC 9110 already
 * requires of it, and `api/client.ts` records the offset here. Where a
 * response carries no usable `Date` we fall back to the smallest
 * `receivedAt − observedAt` seen so far, which is the best estimate
 * available and is at least never worse than trusting the local clock.
 */

let offsetMs: number | null = null;
let source: 'date-header' | 'estimated' | 'none' = 'none';

/** Record the server's `Date` header against the instant we received it. */
export function recordServerDate(header: string | null, receivedAtMs = Date.now()): void {
  if (!header) return;
  const serverMs = Date.parse(header);
  if (Number.isNaN(serverMs)) return;
  offsetMs = serverMs - receivedAtMs;
  source = 'date-header';
}

/** Fallback: the smallest observed lag wins, and only while we have no `Date`. */
export function recordEstimatedOffset(candidateMs: number): void {
  if (source === 'date-header') return;
  if (offsetMs === null || candidateMs > offsetMs) {
    // `candidateMs` here is `observedAt − receivedAt`, so the *largest* is the
    // one from the freshest response.
    offsetMs = candidateMs;
    source = 'estimated';
  }
}

/** Now, on the server's timeline. */
export function serverNow(): number {
  return Date.now() + (offsetMs ?? 0);
}

export function serverClockSource(): typeof source {
  return source;
}

/** Test seam. */
export function resetServerClock(): void {
  offsetMs = null;
  source = 'none';
}
